import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { getPaymentStatus } from '../services/api';
import { PaymentStatusResponse } from '../types/api';

export const PaymentSuccessPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const paymentId = Number(searchParams.get('paymentId') || 0);
  const movieId = Number(searchParams.get('movieId') || 0);
  const [payment, setPayment] = useState<PaymentStatusResponse | null>(null);
  const [attempts, setAttempts] = useState(0);
  const [error, setError] = useState('');

  const watchUrl = useMemo(() => `/watch/${payment?.payment.movieId || movieId}`, [movieId, payment?.payment.movieId]);

  useEffect(() => {
    if (!paymentId) {
      setError('Payment reference is missing.');
      return;
    }

    let active = true;
    const load = async () => {
      try {
        const response = await getPaymentStatus(paymentId);
        if (!active) return;
        setPayment(response);
        if (response.payment.status === 'succeeded' && response.rental) {
          window.setTimeout(() => navigate(watchUrl, { replace: true }), 1200);
          return;
        }
        setAttempts((current) => current + 1);
      } catch {
        if (active) setError('Could not load payment status. Please login and try again.');
      }
    };

    load();
    const timer = window.setInterval(load, 2500);
    return () => {
      active = false;
      window.clearInterval(timer);
    };
  }, [navigate, paymentId, watchUrl]);

  const isReady = payment?.payment.status === 'succeeded' && payment.rental;
  const isWaiting = payment?.payment.status === 'pending' || (!payment && !error);

  return (
    <main className="mx-auto max-w-3xl px-4 py-10 sm:px-6 lg:px-8">
      <div className="rounded-lg border border-[#ffd460]/70 bg-white p-6 text-center shadow-sm">
        <h1 className="text-3xl font-bold text-stone-950">Payment received</h1>
        {isReady && (
          <>
            <p className="mt-3 text-stone-700">Your rental is active. Opening the player now...</p>
            <Link className="mt-6 inline-block rounded-md bg-[#ffd460] px-5 py-3 font-semibold text-stone-950 hover:bg-[#ffc83d]" to={watchUrl}>
              Watch now
            </Link>
          </>
        )}
        {isWaiting && (
          <>
            <p className="mt-3 text-stone-700">We are confirming your payment and activating your rental.</p>
            <p className="mt-2 text-sm text-stone-500">This usually takes a few seconds after the payment provider sends the confirmed webhook callback.</p>
            {attempts > 8 && <p className="mt-4 rounded-md bg-[#fff3c4] p-3 text-sm text-stone-700">Still waiting for the confirmed payment callback. You can stay here or check My rentals shortly.</p>}
          </>
        )}
        {payment?.payment.status === 'failed' && <p className="mt-4 rounded-md bg-red-50 p-3 text-red-700">Payment failed. Your rental was not activated.</p>}
        {error && <p className="mt-4 rounded-md bg-red-50 p-3 text-red-700">{error}</p>}
        <Link className="mt-6 block text-sm font-semibold text-stone-700" to="/rentals">
          My rentals
        </Link>
      </div>
    </main>
  );
};
